import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NavigationService } from 'src/app/Layout/navigation.service';
import { NubService } from 'src/app/Navigation/Nub/nub.service';
import { HotkeyService } from '../../UI-Lib/HotKeys/hotKeys.service';
import { SearchService } from '../search.service';
import { QuickCriteria } from './Criteria/quickCriteria.model';

@Component({
    selector: 'quick-search',
    templateUrl: './quickSearch.component.html',
    styleUrls: ['./quickSearch.component.scss'],
    providers: [HotkeyService],
    standalone: false,
})
export class QuickSearchComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _searchService: SearchService,
        private readonly _navigationService: NavigationService,
        private readonly _nubService: NubService,
        private readonly _hotkeys: HotkeyService,
        private readonly _toastr: ToastrService,
    ) {}

    active = false; // prevent initial animation on page load
    showSearch = false;
    showCriteria = false;
    showResults = false;
    criteria: QuickCriteria;
    navigatingFrom = null;

    private _destroy$: Subject<void> = new Subject();
    private _unregisterQuickSearch: () => void = () => undefined;

    ngOnInit(): void {
        this._searchService.reset();
        this._searchService.resetCriteria();
        this._searchService.resetCriteriaToDefault();

        this.criteria = this._searchService.getCriteria();

        this._unregisterQuickSearch = this._navigationService.registerQuickSearch(
            this.openQuickSearchListener.bind(this),
        );
        this._searchService.criteriaChanged$
            .pipe(takeUntil(this._destroy$))
            .subscribe(this.criteriaChangedListenerVisibility.bind(this));

        this._nubService.showQuickSearch$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this.toggleSearch(null, true);
            });

        this.bindHotKeys();
    }

    ngOnDestroy(): void {
        this._unregisterQuickSearch();
        this._destroy$.next();
        this._destroy$.complete();
    }

    criteriaChangedListenerVisibility(): void {
        this.setShowCriteria(false);
        this.setShowResults(true);
    }

    openQuickSearchListener(navigatingFrom?: string): void {
        this.navigatingFrom = navigatingFrom || null;
        this.resetSearch();

        this._searchService.resetActiveFilterValues();
        this.showSearch = true;
        this._searchService.focusOnFilter(0);
    }

    toggleSearch(event?: Event, tabClicked?: boolean): void {
        if (event) {
            event.stopPropagation();
        }

        this.active = true; // set to true on initial click. no need to set it back
        this.navigatingFrom = null;

        this.showSearch = !this.showSearch;

        if (this.showResults) {
            if (this.showSearch) {
                const clickedOnResultIndex = this._searchService.getClickedOnResultIndex();
                if (clickedOnResultIndex) {
                    this._searchService.focusOnResult(clickedOnResultIndex);
                } else {
                    this._searchService.focusOnElement('.search-results-infinite');
                }
                this.setShowTab(false);
            } else {
                this.setShowTab(true);
            }
        } else {
            this.setShowTab(false);
        }
    }

    setShowCriteria(bool: boolean): void {
        this.showCriteria = bool;
    }

    toggleCriteria(): void {
        this.showCriteria = !this.showCriteria;

        if (this.showCriteria) {
            this._searchService.focusOnFilter(0);
        } else {
            this._searchService.focusOnResult(0);
        }
    }

    setShowResults(bool: boolean): void {
        this.showResults = bool;
    }

    resetClicked(): void {
        this.resetSearch();
    }

    resetSearch(): void {
        this._searchService.reset();

        this.setShowTab(false);

        this.setShowCriteria(true);

        this.setShowResults(false);
    }

    async saveSearch(): Promise<void> {
        this._searchService.setCriteria(this.criteria, false);

        await this._searchService.saveCriteria();
        this._searchService.focusOnFilter(0);
        this._toastr.success('Search criteria successfully saved.');
    }

    setShowTab(bool: boolean): void {
        if (bool) {
            this._nubService.showQuickSearchNub();
        } else {
            this._nubService.hideNub();
        }
    }

    bindHotKeys(): void {
        this._hotkeys.add({ keys: 'alt+c' }).subscribe(() => {
            if (this.showSearch) {
                this.toggleCriteria();
            }
        });
        this._hotkeys.add({ keys: 'alt+r' }).subscribe(() => {
            if (this.showSearch) {
                this.resetSearch();
            }
        });
        this._hotkeys.add({ keys: 'esc' }).subscribe(() => {
            if (this.showSearch) {
                this.toggleSearch();
            }
        });
    }
}
